import React from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import {
    ArAgingBucket,
    AR_AGING_BUCKET_LABELS,
    AR_AGING_BUCKET_ORDER,
} from "@common/types/reports/ar-aging";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { formatMoneyColumnHeader } from "@common/helpers/number";
import { ViewConfig } from "@lib/views";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { makeArAgingSummaryBucketCell } from "./ArAgingSummaryBucketCell";
import { ArAgingSummaryTotalCell } from "./ArAgingSummaryTotalCell";
import { ArAgingSummaryViewRow } from "./arAgingSummaryViewTypes";
import styles from "./ArAgingSummaryTable.module.scss";

const AR_AGING_SUMMARY_CUSTOMER_COLUMN_ID = "customer";
const AR_AGING_SUMMARY_TOTAL_COLUMN_ID = "total";
const CUSTOMER_MIN_SIZE = 240;
const CUSTOMER_MAX_SIZE = 1200;

function bucketColumnId(bucket: ArAgingBucket): string {
    return `bucket-${bucket}`;
}

const renderCustomerCell = React.memo(function ArAgingSummaryCustomerCell({
    row,
}: CellContext<
    ViewTableFeatures,
    ArAgingSummaryViewRow,
    unknown
>): React.ReactElement {
    const { customerName, isSummary } = row.original;
    return (
        <ViewTableCell className={isSummary ? styles.summaryCell : undefined}>
            {customerName}
        </ViewTableCell>
    );
});

type ArAgingSummaryColumnRecord = Record<
    string,
    ViewConfig<ArAgingSummaryViewRow, string, never>["columns"][string]
>;

/**
 * Builds the ViewConfig for the A/R Aging Summary table. Unlike P&L / Trial
 * Balance / Expense by Vendor, the bucket columns are FIXED (the 5
 * `AR_AGING_BUCKET_ORDER` buckets), not built dynamically from a
 * data-driven `periods` array — so this just iterates the fixed order once.
 */
export function buildArAgingSummaryConfig(
    numberFormat?: NumberFormatValues,
): ViewConfig<ArAgingSummaryViewRow, string, never> {
    const columns: ArAgingSummaryColumnRecord = {
        [AR_AGING_SUMMARY_CUSTOMER_COLUMN_ID]: {
            name: "Customer",
            header: "Customer",
            accessorKey: "customerName",
            cell: renderCustomerCell,
            minSize: CUSTOMER_MIN_SIZE,
            maxSize: CUSTOMER_MAX_SIZE,
            immutable: true,
        },
    };

    AR_AGING_BUCKET_ORDER.forEach((bucket) => {
        columns[bucketColumnId(bucket)] = {
            name: AR_AGING_BUCKET_LABELS[bucket],
            header: formatMoneyColumnHeader(
                AR_AGING_BUCKET_LABELS[bucket],
                numberFormat,
            ),
            accessorKey: "amountsByBucket",
            cell: makeArAgingSummaryBucketCell({ bucket }),
            ...AMOUNT_COLUMN_SIZE,
        };
    });

    columns[AR_AGING_SUMMARY_TOTAL_COLUMN_ID] = {
        name: "Total",
        header: formatMoneyColumnHeader("Total", numberFormat),
        accessorKey: "total",
        cell: ArAgingSummaryTotalCell,
        ...AMOUNT_COLUMN_SIZE,
        immutable: true,
    };

    return { columns };
}

/** Builds an ephemeral View object (never persisted to DB). */
export function buildArAgingSummaryView(): View {
    return {
        type: ViewType.AR_AGING_SUMMARY,
        columns: [
            {
                id: AR_AGING_SUMMARY_CUSTOMER_COLUMN_ID,
                options: { pin: "left" as const },
            },
            ...AR_AGING_BUCKET_ORDER.map((bucket) => ({
                id: bucketColumnId(bucket),
            })),
            {
                id: AR_AGING_SUMMARY_TOTAL_COLUMN_ID,
                options: { pin: "right" as const },
            },
        ],
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.LG,
        },
    };
}
